import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, Heart, MessageCircle, Download, Eye, Send, Star, Reply, Instagram, Lock,
} from 'lucide-react';
import { getClientId, getLikedCommentSet, setCommentLiked } from '../../utils/clientIdentity';
import ShareButtons from '../ShareButtons';
import { useAuth } from '../../context/AuthContext';
import { getTierBadge, effectivePlan } from '../../utils/tierBadge';
import { SOCIAL_CARD } from '../../utils/brand';

const API = process.env.REACT_APP_BACKEND_URL;

// "23 Feb 2026 · 4:32 pm" - Melbourne local time, regardless of visitor TZ
const formatPostedAt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Australia/Melbourne',
  });
  const timePart = d.toLocaleTimeString('en-AU', {
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: 'Australia/Melbourne',
  });
  return `${datePart} · ${timePart.toLowerCase()}`;
};

const VideoCard = ({ video }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ views: 0, likes: 0, comments: 0, user_liked: false });
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentName, setCommentName] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // comment_id we're replying to
  const [replyText, setReplyText] = useState('');
  const [replyName, setReplyName] = useState('');
  const [expanded, setExpanded] = useState(false);
  // Meter gate - when the backend returns {locked:true} on POST /view/:id we
  // pause the player and cover it with a subscribe card. {message, kind}
  const [videoLocked, setVideoLocked] = useState(null);
  const clientId = getClientId();

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/video-engagement/stats/${video.id}`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [video.id]);

  const trackView = useCallback(async (videoEl) => {
    try {
      const res = await fetch(`${API}/api/video-engagement/view/${video.id}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 401) {
        // Anonymous post-grace - must sign up
        setVideoLocked({
          kind: 'signup',
          message: 'Videos are for subscribers only. Sign up and pick a plan to watch.',
          cta: 'Subscribe to watch',
          href: '/login',
        });
        if (videoEl) { try { videoEl.pause(); videoEl.currentTime = 0; } catch { /* ignore */ } }
        return;
      }
      const data = await res.json();
      if (data.locked) {
        setVideoLocked({
          kind: 'quota',
          message: data.message || 'Videos are subscribers-only. Start Monthly from $13 to keep watching.',
          cta: 'Subscribe to keep watching',
          href: '/subscribe',
        });
        if (videoEl) { try { videoEl.pause(); videoEl.currentTime = 0; } catch { /* ignore */ } }
        return;
      }
      if (typeof data.views === 'number') {
        setStats((prev) => ({ ...prev, views: data.views }));
      }
    } catch (err) {
      console.error('Failed to track view:', err);
    }
  }, [video.id]);

  const fetchComments = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/video-engagement/comments/${video.id}?client_id=${encodeURIComponent(clientId)}`
      );
      const data = await res.json();
      const liked = getLikedCommentSet();
      // Merge in localStorage liked state so UI is always correct from user's POV
      const merged = data.map((c) => ({
        ...c,
        user_liked: c.user_liked || liked.has(c.id),
      }));
      setComments(merged);
      setCommentsLoaded(true);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  }, [video.id, clientId]);

  const toggleCommentLike = async (commentId) => {
    const current = comments.find((c) => c.id === commentId);
    const nextLiked = !current?.user_liked;
    // Optimistic update (UI + persist to localStorage immediately)
    setCommentLiked(commentId, nextLiked);
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? { ...c, user_liked: nextLiked, likes: Math.max(0, (c.likes || 0) + (nextLiked ? 1 : -1)) }
          : c
      )
    );
    try {
      const res = await fetch(`${API}/api/video-engagement/comment-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment_id: commentId, client_id: clientId }),
      });
      const data = await res.json();
      // Sync the count from server (likes total), but trust localStorage for user_liked
      setCommentLiked(commentId, data.liked);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId ? { ...c, likes: data.likes, user_liked: data.liked } : c
        )
      );
    } catch (err) {
      console.error('Failed to toggle comment like:', err);
      // Revert localStorage on failure
      setCommentLiked(commentId, !nextLiked);
      fetchComments();
    }
  };

  const submitReply = async (parentId) => {
    if (!replyText.trim()) return;
    try {
      const res = await fetch(`${API}/api/video-engagement/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_id: video.id,
          author_name: replyName.trim() || 'Anon',
          comment_text: replyText.trim(),
          parent_id: parentId,
        }),
      });
      if (!res.ok) throw new Error('Reply failed');
      setReplyText('');
      setReplyName('');
      setReplyingTo(null);
      fetchComments();
      fetchStats();
    } catch (err) {
      console.error('Failed to submit reply:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleVideoPlay = (e) => {
    trackView(e?.currentTarget);
  };

  const toggleLike = async () => {
    try {
      const res = await fetch(`${API}/api/video-engagement/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_id: video.id }),
      });
      const data = await res.json();
      setStats((prev) => ({ ...prev, likes: data.likes, user_liked: data.liked }));
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const submitComment = async () => {
    if (!commentText || !commentName) return;
    try {
      await fetch(`${API}/api/video-engagement/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_id: video.id,
          author_name: commentName,
          comment_text: commentText,
        }),
      });
      setCommentText('');
      fetchComments();
      fetchStats();
    } catch (err) {
      console.error('Failed to submit comment:', err);
    }
  };

  const toggleComments = () => {
    setShowComments((prev) => !prev);
    if (!showComments && !commentsLoaded) {
      fetchComments();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = video.video_url;
    link.download = video.title || 'video';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={`bg-zinc-900/80 backdrop-blur-sm border ${
        video.is_exclusive
          ? 'border-yellow-500/60 shadow-[0_0_24px_rgba(250,204,21,0.25)]'
          : 'border-red-900/30 hover:border-red-500/50'
      } rounded-xl overflow-hidden transition-all duration-300`}
      data-testid={`video-card-${video.id}`}
      id={`v-${video.id}`}
    >
      {/* JSON-LD for Google Video search - one per video, keeps rich results
          in sync with the video sitemap. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: video.title,
            description: video.description || video.title,
            thumbnailUrl: video.thumbnail_url || video.video_url,
            uploadDate: video.uploaded_at,
            contentUrl: video.video_url,
            embedUrl: `https://www.outlawmedia.com.au/explicit#v-${video.id}`,
            isFamilyFriendly: false,
            publisher: {
              '@type': 'NewsMediaOrganization',
              name: 'Outlaw Media',
              logo: {
                '@type': 'ImageObject',
                url: SOCIAL_CARD,
              },
            },
            author: { '@type': 'Person', name: 'Ryan Naumenko' },
          }),
        }}
      />
      <div className="aspect-video bg-black relative">
        <video
          src={video.video_url}
          controls
          className="w-full h-full object-cover"
          preload="metadata"
          onPlay={handleVideoPlay}
          data-testid={`video-player-${video.id}`}
        />
        {videoLocked && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/95 backdrop-blur-sm p-5 text-center"
            data-testid={`video-locked-${video.id}`}
          >
            <div className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-3">
              <Lock className="h-3.5 w-3.5" />
              {videoLocked.kind === 'quota' ? 'Subscribers Only' : 'Subscribers Only'}
            </div>
            <h3 className="text-white text-xl sm:text-2xl font-black leading-tight mb-4 max-w-md" style={{ fontFamily: 'Georgia, serif' }}>
              {videoLocked.message}
            </h3>
            <Link
              to={videoLocked.href}
              data-testid={`video-locked-cta-${video.id}`}
              className="inline-flex items-center justify-center px-5 py-3 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.25em] shadow-[0_0_30px_rgba(220,38,38,0.6)]"
            >
              {videoLocked.cta}
            </Link>
            {videoLocked.kind === 'quota' && (
              <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Meter resets on the 1st (Melbourne time)
              </p>
            )}
          </div>
        )}
        {video.is_exclusive && !videoLocked && (
          <div
            className="absolute top-2 right-2 flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 text-black shadow-[0_0_16px_rgba(250,204,21,0.7)]"
            data-testid="exclusive-badge"
          >
            <Star className="h-3 w-3 fill-black" />
            <span className="text-[10px] font-black tracking-widest">EXCLUSIVE</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-lg font-bold text-white mb-2">{video.title}</h3>
        <p className={`text-sm text-gray-400 ${expanded ? '' : 'line-clamp-2'}`}>
          {video.description}
        </p>
        {video.description && video.description.length > 100 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-red-400 hover:text-red-300 mt-2 flex items-center gap-1 transition-colors"
            data-testid="video-description-toggle"
          >
            {expanded ? (
              <>Show less <ChevronUp className="h-3 w-3" /></>
            ) : (
              <>Show more <ChevronDown className="h-3 w-3" /></>
            )}
          </button>
        )}

        <p className="text-xs text-gray-600 mt-2 mb-3" data-testid="video-posted-at">
          {video.source === 'instagram' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-widest text-pink-400/90 uppercase mr-2 align-middle">
              <Instagram className="h-3 w-3" />
              IG
            </span>
          )}
          {formatPostedAt(video.uploaded_at)}
        </p>

        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-red-900/20">
          <div
            className="flex items-center gap-1 px-3 py-2 rounded bg-zinc-800/80 text-gray-300"
            data-testid="video-view-count-chip"
          >
            <Eye className="h-4 w-4" />
            <span className="text-xs font-bold tabular-nums" data-testid="video-view-count">
              {stats.views.toLocaleString()}
            </span>
          </div>

          <button
            onClick={toggleLike}
            data-testid="video-like-btn"
            className={`flex items-center gap-1 px-3 py-2 rounded transition-colors ${
              stats.user_liked
                ? 'bg-red-600 text-white'
                : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
            }`}
          >
            <Heart className={`h-4 w-4 ${stats.user_liked ? 'fill-current' : ''}`} />
            <span className="text-xs font-bold">{stats.likes}</span>
          </button>

          <button
            onClick={toggleComments}
            data-testid="video-comments-toggle"
            className="flex items-center gap-1 px-3 py-2 rounded bg-zinc-800 text-gray-400 hover:bg-zinc-700 transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            <span className="text-xs font-bold">{stats.comments}</span>
          </button>

          <div className="flex items-center justify-center" data-testid="video-share-wrapper">
            <ShareButtons
              url={`${window.location.origin}/explicit?v=${video.id}`}
              title={video.title}
              text={video.description || ''}
              testId={`video-share-${video.id}`}
            />
          </div>

          <button
            onClick={handleDownload}
            data-testid="video-download-btn"
            className="flex items-center gap-1 px-3 py-2 rounded bg-zinc-800 text-gray-400 hover:bg-zinc-700 transition-colors ml-auto"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>

        {showComments && (
          <div className="mt-3" data-testid="video-comments-section">
            <p className="text-[10px] text-emerald-400/80 mb-2 leading-snug">
              🔒 Anonymous - we don't store your name, IP, or any personal info in our databases.
            </p>
            <div className="mb-3 space-y-2">
              {user ? (
                <div
                  data-testid="video-comment-identity"
                  className="flex items-center gap-2 rounded border border-zinc-800 bg-black/40 px-2.5 py-1.5"
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover border border-zinc-700 shrink-0" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-[10px] font-black text-red-400 shrink-0">
                      {(user.display_name || 'O').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 leading-none">Posting as</p>
                    {(() => {
                      const badge = getTierBadge(effectivePlan(user));
                      return (
                        <p
                          className={'text-xs font-black truncate mt-0.5 ' + badge.nameClass}
                          style={badge.nameStyle || undefined}
                          title={badge.label}
                        >
                          {badge.emoji ? (
                            <span aria-hidden className={'mr-1 ' + badge.emojiClass}>{badge.emoji}</span>
                          ) : null}
                          {user.display_name || 'Outlaw Reader'}
                        </p>
                      );
                    })()}
                  </div>
                  <Link
                    to="/account"
                    className="text-[10px] font-black uppercase tracking-[0.2em] text-red-400 hover:text-red-300 shrink-0"
                  >
                    Change →
                  </Link>
                </div>
              ) : (
                <input
                  type="text"
                  placeholder="Your name"
                  value={commentName}
                  onChange={(e) => setCommentName(e.target.value)}
                  className="w-full bg-black/50 border border-red-900/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                  data-testid="comment-name-input"
                />
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && submitComment()}
                  className="flex-1 bg-black/50 border border-red-900/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                  data-testid="comment-text-input"
                />
                <button
                  onClick={submitComment}
                  data-testid="comment-submit-btn"
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="comments-list">
              {(() => {
                const parents = comments.filter((c) => !c.parent_id);
                const repliesByParent = comments.reduce((acc, c) => {
                  if (c.parent_id) {
                    if (!acc[c.parent_id]) acc[c.parent_id] = [];
                    acc[c.parent_id].push(c);
                  }
                  return acc;
                }, {});

                const renderComment = (comment, isReply = false) => {
                  const badge = getTierBadge(effectivePlan(comment));
                  return (
                  <div
                    key={comment.id}
                    data-testid={`comment-${comment.id}`}
                    className={`rounded p-2 ${isReply ? 'bg-black/20 border-l-2 border-red-700/40 ml-6' : 'bg-black/30'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {comment.author_avatar_url ? (
                            <img src={comment.author_avatar_url} alt="" className="h-5 w-5 rounded-full object-cover border border-zinc-700 shrink-0" />
                          ) : null}
                          <p
                            className={'text-xs font-bold truncate ' + (badge.emoji ? badge.nameClass : 'text-red-400')}
                            style={badge.nameStyle || undefined}
                            title={badge.label}
                          >
                            {badge.emoji ? (
                              <span aria-hidden className={'mr-1 ' + badge.emojiClass}>{badge.emoji}</span>
                            ) : null}
                            {comment.author_name}
                          </p>
                        </div>
                        <p className="text-sm text-gray-300 break-words">{comment.comment_text}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <p className="text-xs text-gray-600">
                            {formatPostedAt(comment.created_at) || new Date(comment.created_at).toLocaleString()}
                            {comment.status === 'pending' && (
                              <span className="ml-2 text-yellow-400/80">· awaiting moderation</span>
                            )}
                          </p>
                          {!isReply && (
                            <button
                              onClick={() => {
                                setReplyingTo(replyingTo === comment.id ? null : comment.id);
                                setReplyText('');
                              }}
                              data-testid={`comment-reply-btn-${comment.id}`}
                              className="text-xs text-gray-500 hover:text-red-300 font-bold inline-flex items-center gap-1 transition-colors"
                            >
                              <Reply className="h-3 w-3" />
                              Reply
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleCommentLike(comment.id)}
                        data-testid={`comment-like-${comment.id}`}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-colors shrink-0 ${
                          comment.user_liked
                            ? 'bg-red-600/30 text-red-400'
                            : 'bg-zinc-800 text-gray-400 hover:text-red-300'
                        }`}
                        aria-label="Like this comment"
                      >
                        <Heart className={`h-3.5 w-3.5 ${comment.user_liked ? 'fill-red-500' : ''}`} />
                        <span className="tabular-nums">+{comment.likes || 0}</span>
                      </button>
                    </div>

                    {!isReply && replyingTo === comment.id && (
                      <div className="mt-2 pl-2 border-l-2 border-red-700/40 space-y-1.5" data-testid={`reply-form-${comment.id}`}>
                        {user ? (
                          <div className="flex items-center gap-2 rounded border border-zinc-800 bg-black/40 px-2 py-1">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" className="h-5 w-5 rounded-full object-cover border border-zinc-700 shrink-0" />
                            ) : (
                              <div className="h-5 w-5 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-[9px] font-black text-red-400 shrink-0">
                                {(user.display_name || 'O').slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">As</span>
                            <span className="text-[11px] font-black text-white truncate flex-1">{user.display_name || 'Outlaw Reader'}</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            placeholder="Your name (or leave blank for Anon)"
                            value={replyName}
                            onChange={(e) => setReplyName(e.target.value)}
                            className="w-full bg-black/50 border border-red-900/30 text-white px-2 py-1.5 rounded text-xs focus:outline-none focus:border-red-500"
                            data-testid={`reply-name-${comment.id}`}
                          />
                        )}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder={`Reply to ${comment.author_name}...`}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && submitReply(comment.id)}
                            className="flex-1 bg-black/50 border border-red-900/30 text-white px-2 py-1.5 rounded text-xs focus:outline-none focus:border-red-500"
                            data-testid={`reply-text-${comment.id}`}
                            autoFocus
                          />
                          <button
                            onClick={() => submitReply(comment.id)}
                            data-testid={`reply-submit-${comment.id}`}
                            className="bg-red-600 hover:bg-red-700 text-white px-2.5 py-1.5 rounded transition-colors"
                            aria-label="Send reply"
                          >
                            <Send className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {(repliesByParent[comment.id] || []).length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {repliesByParent[comment.id].map((reply) => renderComment(reply, true))}
                      </div>
                    )}
                  </div>
                );
                };

                return parents.map((c) => renderComment(c, false));
              })()}
              {comments.length === 0 && (
                <p className="text-xs text-gray-600 text-center py-2">No comments yet</p>
              )}
            </div>

            {/* Instagram comments deliberately removed - IG scraping is too
                unreliable; engagement is now Outlaw Media internal-only */}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoCard;
